import elephantLogomark from "$assets/elephant-logomark.svg?url";
import { linkToSignOut } from "$routes/_lib/links";
import {
  AppBar,
  Box,
  Button,
  styled,
  Toolbar,
  Typography,
} from "@material-ui/core";
import { ExitToApp } from "@material-ui/icons";
import { Link, Outlet } from "react-router-dom";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_ICON_ACTIVE,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../_lib/epocus-palette";
import { EpocusThemeProvider } from "../_lib/epocus-theme-provider";
import { AccessDenied } from "../_lib/rbac/access-denied";
import { useCanAdministerEpocus } from "./_lib/use-can-administer-epocus";
import { linkToEpocusAdmin } from "./links";

const Shell = styled(Box)({
  display: "flex",
  flexDirection: "column",
  height: "100dvh",
  backgroundColor: EPOCUS_BG,
  "@supports not (height: 100dvh)": {
    height: "100vh",
  },
});

const ContentArea = styled(Box)({
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
});

const StyledAppBar = styled(AppBar)({
  backgroundColor: EPOCUS_WHITE,
  boxShadow: `0 1px 0 0 ${EPOCUS_BORDER}`,
  color: "inherit",
  "& .MuiToolbar-root": {
    minHeight: 56,
    gap: 12,
  },
});

const LogoBox = styled(Box)({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: EPOCUS_ICON_ACTIVE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
});

const LogoImg = styled("img")({
  width: 20,
  height: 20,
  filter: "brightness(0) invert(1)",
});

const BrandGroup = styled(Link)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  textDecoration: "none",
  color: "inherit",
});

const AppName = styled(Typography)({
  color: EPOCUS_ICON_ACTIVE,
  fontWeight: 700,
  fontSize: rem(20),
});

const AdminBadge = styled("span")({
  fontSize: rem(12),
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
});

const AdminShell: FCwC = ({ children }) => (
  <Shell>
    <StyledAppBar position="sticky" elevation={0}>
      <Toolbar>
        <BrandGroup to={linkToEpocusAdmin()} aria-label="Go to admin home">
          <LogoBox>
            <LogoImg src={elephantLogomark} alt="" />
          </LogoBox>
          <AppName variant="h6">ePOCUS</AppName>
          <AdminBadge>Admin</AdminBadge>
        </BrandGroup>
        <Box flex={1} />
        <Button
          component={Link}
          to={linkToSignOut()}
          startIcon={<ExitToApp />}
          style={{ textTransform: "none" }}
        >
          Log out
        </Button>
      </Toolbar>
    </StyledAppBar>
    <ContentArea>{children}</ContentArea>
  </Shell>
);

export const EpocusAdminLayout: FCwC = ({ children = <Outlet /> }) => {
  const canAdminister = useCanAdministerEpocus();

  if (!canAdminister) {
    return (
      <EpocusThemeProvider>
        <Shell>
          <AccessDenied />
        </Shell>
      </EpocusThemeProvider>
    );
  }

  return (
    <EpocusThemeProvider>
      <AdminShell>{children}</AdminShell>
    </EpocusThemeProvider>
  );
};
